import { useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';

interface BulkScrapeConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  pages: string[];
  force: boolean;
  priority: number;
  onConfirm: () => void;
  isCreating: boolean;
}

export function BulkScrapeConfirmDialog({
  open,
  onOpenChange,
  pages,
  force,
  priority,
  onConfirm,
  isCreating,
}: BulkScrapeConfirmDialogProps) {
  // Group pages by company (extract company slug from dot notation)
  const groupedPages = useMemo(() => {
    const groups: Record<string, string[]> = {};
    pages.forEach(slug => {
      // Extract company slug (before the first dot, or entire slug if no dot)
      const companySlug = slug.includes('.') ? slug.split('.')[0] : slug;
      if (!groups[companySlug]) {
        groups[companySlug] = [];
      }
      groups[companySlug].push(slug);
    });
    return groups;
  }, [pages]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Confirm Bulk Scrape Tasks</DialogTitle>
          <DialogDescription>
            Review the pages that will be scraped
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4 flex-1 overflow-hidden">
          {/* Summary */}
          <div className="grid gap-2 p-3 rounded-lg bg-muted/50 border text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Total Pages:</span>
              <span className="font-semibold">{pages.length}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Task Type:</span>
              <Badge variant="secondary">SCRAPE</Badge>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Priority:</span>
              <span>{priority}</span>
            </div>
            {force && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">Force:</span>
                <Badge variant="destructive">Enabled</Badge>
              </div>
            )}
          </div>

          {/* Pages List */}
          <div className="flex-1 overflow-hidden">
            <h4 className="text-sm font-medium mb-2">Pages by Company:</h4>
            <ScrollArea className="h-[300px] border rounded-md p-3">
              {Object.entries(groupedPages).map(([companySlug, pageSlugs]) => (
                <div key={companySlug} className="mb-4">
                  <h5 className="font-medium text-sm mb-1">{companySlug}</h5>
                  <ul className="list-disc list-inside pl-2 space-y-0.5">
                    {pageSlugs.map(pageSlug => (
                      <li key={pageSlug} className="text-xs text-muted-foreground font-mono">
                        {pageSlug}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </ScrollArea>
          </div>
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isCreating}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={onConfirm}
            disabled={isCreating}
          >
            {isCreating ? `Creating ${pages.length} tasks...` : `Create ${pages.length} Tasks`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
